/**
 * Deloitte File Service
 * Service for handling Deloitte Excel file uploads and mapping to templates
 */

import { pythonBaseUrl } from "../App_Settings/app_settings";
import { getUserIdFromLocalStorage } from "../API_Endpoints/APIEndpoints";

/**
 * Step 1: Upload Excel file for mapping (uploadType 13)
 * @param file - Excel file to upload
 * @param fileId - Optional GUID for filename
 * @returns Promise with file_id and upload response
 */
export const uploadExcelForMapping = async (file: File, fileId?: string) => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    if (fileId) {
      formData.append("filename", fileId);
    }
    formData.append("original_filename", file.name);
    formData.append("organization_id", getUserIdFromLocalStorage().organizationId);
    formData.append("user_id", getUserIdFromLocalStorage().userId);

    const userToken = getUserIdFromLocalStorage().token;

    const response = await fetch(`${pythonBaseUrl}/Excel/Upload-Excel-For-Mapping`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
      body: formData,
    });

    if (!response.ok) {
      // Parse error response body to get specific error messages (e.g., malware detection)
      let errorMessage = `Failed to upload Excel file: ${response.status} - ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData.error) {
          // Use specific error message from API (e.g., "Malware detected")
          errorMessage = errorData.details || errorData.error;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        }
      } catch {
        // If not JSON, try reading as text
        try {
          const errorText = await response.text();
          if (errorText) {
            errorMessage = `${errorMessage}. ${errorText}`;
          }
        } catch {
          // Use default error message
        }
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    return {
      success: true,
      data: result,
      file_id: result.file_id || fileId,
    };
  } catch (error) {
    console.error("❌ Error uploading Excel file for mapping:", error);
    throw error;
  }
};

/**
 * Step 2: Map uploaded Excel to template
 * @param fileId - The file_id from upload response
 * @param altVendorIDValues - Optional AltVendorID values for upload type 13
 * @returns Promise with mapping result
 */
export const mapExcelToTemplate = async (
  fileId: string,
  altVendorIDValues?: { AltVendorID1?: string; AltVendorID2?: string; AltVendorID3?: string }
) => {
  try {
    const formData = new FormData();
    formData.append("file_id", fileId);
    formData.append("organization_id", getUserIdFromLocalStorage().organizationId);
    formData.append("user_id", getUserIdFromLocalStorage().userId);
    formData.append("business_unit", "Demo");

    // Add AltVendorID values if provided
    if (altVendorIDValues) {
      if (altVendorIDValues.AltVendorID1)
        formData.append("alt_vendor_id_1", altVendorIDValues.AltVendorID1);
      if (altVendorIDValues.AltVendorID2)
        formData.append("alt_vendor_id_2", altVendorIDValues.AltVendorID2);
      if (altVendorIDValues.AltVendorID3)
        formData.append("alt_vendor_id_3", altVendorIDValues.AltVendorID3);
    }

    const userToken = getUserIdFromLocalStorage().token;

    const response = await fetch(`${pythonBaseUrl}/Excel/Map-Excel-To-Template`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to map Excel to template: ${response.status} - ${response.statusText}. ${errorText}`
      );
    }

    const result = await response.json();
    return {
      success: true,
      data: result,
    };
  } catch (error) {
    console.error("❌ Error mapping Excel to template:", error);
    throw error;
  }
};
